'use client';

import React, { PropsWithChildren } from 'react';
import Image from 'next/image';
import * as Content from '@/components/content';
import CapsuleButton from '@/components/button';
import { useLocale } from 'next-intl';

import rqform from '@images/materials_rqform.webp';
import consentform from '@images/materials_consentform.webp';
import sample from '@images/materials_sample.webp';
import airwayBill from '@images/materials_airway_bill.webp';
import declarationLetter from '@images/materials_declaration_letter.webp';
import fedex from '@images/materials_fedex.webp';
import informedConsent from '@images/materials_informed_consent.webp';
import invoice from '@images/materials_invoice.webp';
import { cn } from '@lib/utils';
import { TrackedAnchor } from '@/components/ancher';
import { getWpUploadsCdnUrl } from '@/utils';

const { Card } = Content;

const CardWrapper = ({
  children,
  className,
}: PropsWithChildrenAndClassName) => (
  <div
    className={cn(
      'grid grid-rows-3 gap-4 md:grid-cols-3 md:grid-rows-1 md:gap-5',
      className,
    )}
  >
    {children}
  </div>
);
const WhiteCard = ({ children }: PropsWithChildren) => (
  <Card className="flex flex-col bg-white pb-0">{children}</Card>
);

const KoreaMaterials = () => (
  <Content.Section>
    <Content.Title title={'샘플과 함께 준비해야 하는 것들'} />
    <CardWrapper>
      <WhiteCard>
        <Card.Title>3billion 검사의뢰서 1부</Card.Title>
        <Card.Description>검사 주문 후 포탈에서 출력하세요.</Card.Description>
        <Image className="mt-auto" src={rqform} alt="Requisition Form" />
      </WhiteCard>
      <WhiteCard>
        <Card.Title>유전자 검사 동의서 1부</Card.Title>
        <Card.Description className="mb-[16px]">
          검사대상자/의료진의 서명이 된 동의서
        </Card.Description>
        <CapsuleButton variant="secondary" asChild>
          <TrackedAnchor
            href={getWpUploadsCdnUrl(
              '2024/06/02021713/유전자검사-동의서-1.pdf',
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="self-start"
            label="materials_agreement-form-download"
            position="main_content"
          >
            동의서 양식 다운로드
          </TrackedAnchor>
        </CapsuleButton>
        <Image className="mt-auto" src={consentform} alt="Consent Form" />
      </WhiteCard>
      <WhiteCard>
        <Card.Title>3billion ID가 기재된 샘플</Card.Title>
        <Card.Description>
          검사의뢰서 우측 상단에 보이는 9~10자리 ID를 샘플에 기입하세요.
          <br />
          예시) EPA24-ABCD
        </Card.Description>
        <Image className="mt-auto" src={sample} alt="Sample" />
      </WhiteCard>
    </CardWrapper>
  </Content.Section>
);

const ShipmentDocuments = () => (
  <Content.Section>
    <Content.Title title={'Shipment Documents'} />
    <CardWrapper>
      <WhiteCard>
        <Card.Title className="pb-[24px] md:pb-[64px]">
          Airway bill
          <br />1 copy
        </Card.Title>
        <Image className="mt-auto" src={airwayBill} alt="airway bill" />
      </WhiteCard>
      <WhiteCard>
        <Card.Title className="pb-[24px] md:pb-[64px]">
          Proforma Invoice
          <br />3 copies
        </Card.Title>
        <Image className="mt-auto" src={invoice} alt="invoice" />
      </WhiteCard>
      <WhiteCard>
        <Card.Title className="pb-[24px] md:pb-[64px]">
          Declaration Letter
          <br />2 copies
        </Card.Title>
        <Image
          className="mt-auto"
          src={declarationLetter}
          alt="declaration letter"
        />
      </WhiteCard>
    </CardWrapper>
  </Content.Section>
);
const SampleDocuments = () => (
  <Content.Section>
    <Content.Title title={'Sample Documents'} />
    <CardWrapper className="grid-rows-2">
      <WhiteCard>
        <Card.Title className="pb-[24px] md:pb-[64px]">
          Informed Consent <br />
          Form(s) 1 copy
        </Card.Title>
        <Image
          className="mt-auto"
          src={informedConsent}
          alt="informed consent "
        />
      </WhiteCard>
      <WhiteCard>
        <Card.Title className="pb-[24px] md:pb-[64px]">Sample(s)</Card.Title>
        <Image className="mt-auto" src={fedex} alt="fedex" />
      </WhiteCard>
      <div className="hidden md:block" />
    </CardWrapper>
  </Content.Section>
);

function Materials() {
  const locale = useLocale();
  const isKorea = locale === 'ko';

  if (isKorea) return <KoreaMaterials />;
  return (
    <>
      <ShipmentDocuments />
      <SampleDocuments />
    </>
  );
}

export default Materials;
